/**
 * Global Company Filter Context
 * Provides shared company selection state across Dashboard, Cost Calculator, and AI Chat
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import apiClient from "@/lib/api";

const CompanyFilterContext = createContext(null);

export const CompanyFilterProvider = ({ children, isAuthenticated }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [loading, setLoading] = useState(true);

  // Fetch companies when authenticated
  useEffect(() => {
    const fetchCompanies = async () => {
      // P1 SECURITY: Auth is handled via httpOnly cookies, check isAuthenticated prop
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/api/companies`);
        setCompanies(response.data || []);
      } catch (error) {
        // Silent fail - companies will just be empty
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, [isAuthenticated]);

  // Persist selection to sessionStorage
  useEffect(() => {
    if (selectedCompany) {
      sessionStorage.setItem("selectedCompany", selectedCompany);
    }
  }, [selectedCompany]);

  // Restore selection from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem("selectedCompany");
    if (saved) {
      setSelectedCompany(saved);
    }
  }, []);

  const selectCompany = useCallback((companyId) => {
    setSelectedCompany(companyId);
  }, []);

  const getSelectedCompanyName = useCallback(() => {
    if (selectedCompany === "all") return "All Companies";
    const company = companies.find(c => c.company_id === selectedCompany);
    return company?.company_name || selectedCompany;
  }, [selectedCompany, companies]);

  const value = {
    companies,
    selectedCompany,
    selectCompany,
    loading,
    getSelectedCompanyName,
    // Helper to get company_id for API calls (null if "all")
    getApiCompanyId: () => selectedCompany === "all" ? null : selectedCompany
  };

  return (
    <CompanyFilterContext.Provider value={value}>
      {children}
    </CompanyFilterContext.Provider>
  );
};

export const useCompanyFilter = () => {
  const context = useContext(CompanyFilterContext);
  if (!context) {
    throw new Error("useCompanyFilter must be used within a CompanyFilterProvider");
  }
  return context;
};

export default CompanyFilterContext;
